import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { ethers } from 'ethers';
import { useAccount } from 'wagmi';

import { selectSelectedInstrument, selectGasPrice } from 'store/entities/market/selectors';
import { balanceSelector } from 'store/entities/wallet/selectors';

import { countMinMaxAmount, getOrderAmountValidationMessages } from 'helpers';

import { OrderSide, OrderType } from 'types';

export const useOrderAmountValidation = (
  amount: string,
  limitPrice: number,
  tokenBalance: number,
  orderSide: OrderSide,
  orderType: OrderType,
  currency: string,
  focusInput: boolean
): string => {
  const { address } = useAccount();
  const nativeToken = useSelector(balanceSelector);
  const gasPrice = useSelector(selectGasPrice);
  const instrument = useSelector(selectSelectedInstrument);

  const [error, setError] = useState('');

  const validationMessages = getOrderAmountValidationMessages(_t);

  useEffect(() => {
    if (!address && focusInput) {
      setError(validationMessages.walletNotConnected);
      return;
    }

    if (amount && instrument && nativeToken) {
      const hardcodedTxGas = 0.05;
      const gas = Number(ethers.utils.formatEther(gasPrice.toString()));
      const { maxQuantity, minQuantity } = countMinMaxAmount(limitPrice, instrument, orderType, orderSide);
      const amountNum = Number(amount);
      const nativeTokenBalance = Number(nativeToken);
      const isNativeTokenSelling =
        orderSide === OrderSide.Sell && currency === 'AVAX' && amountNum + gas + hardcodedTxGas > nativeTokenBalance;

      // If amount exceeds user balance
      if (amountNum > tokenBalance) {
        setError(validationMessages.walletBalanceExceeds);
        return;
      }

      // If amount less than min price
      if (amountNum < minQuantity) {
        setError(`${validationMessages.minAmountAxceeds} ${minQuantity}`);
        return;
      }

      // If amount greater than max price
      if (amountNum > maxQuantity) {
        setError(`${validationMessages.maxAmountAxceeds} ${maxQuantity}`);
        return;
      }

      // If sufficient amount for tx fee
      if (isNativeTokenSelling || nativeTokenBalance < gas) {
        setError(validationMessages.insufficientNativeToken);
        return;
      }
    }

    // Default clear error
    setError(validationMessages.default);
  }, [amount, limitPrice, tokenBalance, instrument, address, focusInput]);

  return error;
};
